import React, { useCallback, useMemo, useState, type JSX } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import moment from 'moment';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { activateJourneyInstance, deactivateJourneyInstance, saveJourneyInstance } from 'store/journey/journey.thunk';
import { getIsJourneyActiveOrScheduled, getJourneyStatusLabels } from 'store/journey/journey.selector';
import { JOURNEY_STATUSES, JourneyState } from 'store/journey/journey.type';
import { getIsAudienceEmpty } from 'store/rule/rule.selector';
import { getNonBlockingErrorCategories } from 'validators/ExperienceCanvas/journey.validator';
import { ActionTypes } from 'store/actionTypes';
import { store } from 'store/store';

import { useThunkDispatch as useDispatch, useAppSelector as useSelector } from 'hooks/reduxHooks';
import useConfirmModal from 'hooks/useConfirmModal';
import { useContextDataSlideOut } from 'components/ExperienceCanvas/ContextDataSlideOut/ContextDataSlideOutContext';
import FileAPI from 'services/api/file.api';
import { convertDateToTimezone } from 'helpers/templated-experience.helper';
import { buildLaunchConfirmationDialogProps } from 'components/ExperienceCanvas/HeaderLayout/PrimaryHeader/LaunchConfirmationDialog';
import { ReactComponent as IconLaunchRocket } from 'assets/images/image-launch-rocket.svg';
import { ReactComponent as IconLanguage } from 'assets/icons/language-v2.svg';
import LaunchIcon from 'pages/ExperienceCanvas/assets/icons/LaunchIcon';
import ErrorIcon1 from 'pages/ExperienceCanvas/assets/icons/ErrorIcon1';
import DeactivateIcon from 'pages/ExperienceCanvas/assets/icons/DeactivateIcon';

import { FILE_STATUS, TExperienceCanvasRouteParams } from 'pages/ExperienceCanvas/types';
import { PaginationResponse } from 'interface/shared/api.interface';
import { CtxDataFile } from 'interface/file/file.interface';
import { REFETCH_INTERVALS } from 'constants/shared';

import useTransactionalConfirmModal from './useTransactionalConfirmModal';
import {
  buildCantEditActiveDialog,
  buildCantSaveInvalidDialog,
  buildDeactivateDialog,
  buildExpiredScheduleDialog,
  buildLaunchImmediatelyDialog,
  buildScheduleFutureDialog,
} from '../modals/confirmDialogs';

const HEADER_ICON_FILL = '#2371f1';
const ERROR_ICON_FILL = '#EB7E24';
const DEACTIVATE_ICON_FILL = '#FF541E';

export type UseJourneyActionsParams = {
  journeyInstance: JourneyState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch: (showLoading?: any) => Promise<void>;
  /** Whether the experience name is currently invalid (empty / non-unique); blocks save & launch. */
  experienceNameError?: string;
};

export type UseJourneyActions = {
  launch: () => Promise<void>;
  deactivate: () => Promise<void>;
  save: () => Promise<void>;
  exit: () => void;
  /** A single derived busy flag, replacing v1's three overlapping booleans. */
  isBusy: boolean;
  isJourneyActiveOrScheduled: boolean;
  /** Render-fn for the transactional confirm modal — mount once in the v2 header. */
  ConfirmModalSlot: () => JSX.Element | null;
  /** Render-fn for the launch confirmation modal — mount once in the v2 header. */
  LaunchConfirmModalSlot: () => JSX.Element | null;
};

/**
 * v2-native launch / deactivate / save / exit orchestration over the existing journey thunks and
 * selectors. Instantiate **once** per FlowBuilderV2 page and share the result with both the v2
 * header (Save / Exit) and the unified launch popover (Launch / Deactivate) — a second instance
 * would create a duplicate confirm-modal session and double the context-file polling.
 *
 * Improvements over v1's `PrimaryHeader`:
 *  - one derived `isBusy` instead of three local booleans (`isLaunching`, `isJourneySaving`,
 *    `experienceIsLaunching`);
 *  - transactional dialogs are pure props driven through an await-based `showConfirmModal()`
 *    rather than callback-stuffed `primaryButton.onClick` closures;
 *  - launch success is detected from the dispatch result / post-dispatch store state.
 */
export default function useJourneyActions({
  journeyInstance,
  refetch,
  experienceNameError = '',
}: UseJourneyActionsParams): UseJourneyActions {
  const history = useHistory();
  const { pid: projectId } = useRouteMatch<TExperienceCanvasRouteParams>().params;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const contextDataSlideOut = useContextDataSlideOut();
  const fileAPI = useMemo(() => new FileAPI(), []);

  const [isBusy, setIsBusy] = useState(false);

  // --- Derived journey state -------------------------------------------------
  const journeyStatusLabels = useSelector((state) => getJourneyStatusLabels(state));
  const isJourneyActiveOrScheduled = useSelector((state) => getIsJourneyActiveOrScheduled(state.te));
  const isAudienceEmpty = useSelector((state) => !!getIsAudienceEmpty(state.te, 0 /* step index fudged to 0 */));
  const nonBlockingErrorCategories = useSelector((state) => getNonBlockingErrorCategories(state));
  const hasLaunchBlockingError = useSelector((state) => !!state.te.journey.hasLaunchBlockingError);
  const filesUUID = useSelector((state) => state.te.journey.metadata?.filesUUID || '');
  const isListBasedTemplate = useSelector((state) =>
    state.te.journey.steps.reduce((prev, curr) => prev || curr.type === 'list-based', false),
  );

  // --- Context-data file polling (list-based templates only) -----------------
  const { data: processedCtxFilesRes } = useQuery<PaginationResponse<CtxDataFile>>({
    queryKey: ['ctx-data-file', filesUUID, FILE_STATUS.PROCESSING_SUCCESS],
    queryFn: async () =>
      await fileAPI.getAllCtxDataFiles({ offset: 0, limit: 1, tag: filesUUID, status: FILE_STATUS.PROCESSING_SUCCESS }),
    refetchInterval: REFETCH_INTERVALS.FILE_PROCESSING,
    enabled: !!filesUUID && isListBasedTemplate,
  });
  const { data: uploadedCtxFilesRes } = useQuery<PaginationResponse<CtxDataFile>>({
    queryKey: ['ctx-data-file', filesUUID, FILE_STATUS.UPLOADED],
    queryFn: async () =>
      await fileAPI.getAllCtxDataFiles({ offset: 0, limit: 1, tag: filesUUID, status: FILE_STATUS.UPLOADED }),
    refetchInterval: REFETCH_INTERVALS.FILE_PROCESSING,
    enabled: !!filesUUID && isListBasedTemplate,
  });

  const numUploadedFiles = uploadedCtxFilesRes?.data?.length;
  const numProcessedFiles = processedCtxFilesRes?.data?.length;
  // misnomer, this flag is true when there are no files to process and no processed files
  const hasPendingFiles = numUploadedFiles ? numUploadedFiles < 1 : numProcessedFiles ? numProcessedFiles < 1 : true;
  const showContextFilesLaunchModal = isListBasedTemplate && !!filesUUID && !hasPendingFiles;

  // --- Launch confirmation modal (snapshot-based copy) -----------------------
  const launchConfirmationDialogProps = useMemo(
    () =>
      buildLaunchConfirmationDialogProps({
        showContextFilesLaunchModal,
        isAudienceEmpty,
        hasLocalizationWarning: nonBlockingErrorCategories.has('localization'),
        icon: React.createElement(IconLaunchRocket),
        languageBannerIcon: React.createElement(IconLanguage),
      }),
    [showContextFilesLaunchModal, isAudienceEmpty, nonBlockingErrorCategories],
  );
  const [LaunchConfirmModalSlot, showLaunchConfirmModal] = useConfirmModal({
    ...launchConfirmationDialogProps,
    isLoading: isBusy,
  });

  // --- Transactional confirm modal (await-based) -----------------------------
  const { ConfirmModalSlot, showConfirmModal } = useTransactionalConfirmModal();

  // --- Actions ---------------------------------------------------------------
  const exit = useCallback(() => {
    history.push(`/project/${projectId}/${window.location.search}`);
  }, [history, projectId]);

  const save = useCallback(async () => {
    if (experienceNameError.length) return;

    const schedule = journeyInstance?.schedule;
    const activatedAt = journeyInstance?.activatedAt;
    const isJourneyScheduled = journeyInstance.status === JOURNEY_STATUSES.SCHEDULED;

    if (isJourneyScheduled && !schedule?.start && !schedule?.end) {
      const confirmed = await showConfirmModal(
        buildLaunchImmediatelyDialog(React.createElement(LaunchIcon, { fill: HEADER_ICON_FILL })),
      );
      if (confirmed) {
        dispatch({ type: 'UPDATE_JOURNEY', payload: { status: JOURNEY_STATUSES.DRAFT } });
      }
      return;
    }

    if (isJourneyActiveOrScheduled && activatedAt) {
      const activationDate = moment(activatedAt * 1000)?.format('MMM DD, YYYY [at] h:mmA');
      await showConfirmModal(
        buildCantEditActiveDialog(activationDate, React.createElement(ErrorIcon1, { fill: ERROR_ICON_FILL })),
      );
      return;
    }

    if (journeyInstance.status === JOURNEY_STATUSES.INACTIVE && journeyStatusLabels.launch.isDisabled) {
      await showConfirmModal(buildCantSaveInvalidDialog(React.createElement(ErrorIcon1, { fill: ERROR_ICON_FILL })));
      return;
    }

    setIsBusy(true);
    try {
      await dispatch(saveJourneyInstance());
      if (journeyInstance.id) await refetch(false);
    } finally {
      setIsBusy(false);
    }
  }, [
    experienceNameError,
    journeyInstance,
    isJourneyActiveOrScheduled,
    journeyStatusLabels.launch.isDisabled,
    showConfirmModal,
    dispatch,
    refetch,
  ]);

  const launch = useCallback(async () => {
    if (
      isBusy ||
      experienceNameError.length ||
      journeyStatusLabels.launch.isDisabled ||
      hasLaunchBlockingError ||
      (isListBasedTemplate && hasPendingFiles)
    ) {
      return;
    }

    const schedule = journeyInstance?.schedule;
    const scheduleStartUnix = convertDateToTimezone(schedule?.start || 0, schedule?.timezone || '');
    const scheduleEndUnix = convertDateToTimezone(schedule?.end || 0, schedule?.timezone || '');
    const currentTimeUnix = moment().unix();

    if (schedule?.start && scheduleStartUnix.local_unix > currentTimeUnix) {
      const confirmed = await showConfirmModal(
        buildScheduleFutureDialog({ schedule, isListBasedTemplate, hasPendingFiles, isAudienceEmpty }),
      );
      if (confirmed) {
        setIsBusy(true);
        try {
          await dispatch(saveJourneyInstance(JOURNEY_STATUSES.SCHEDULED));
          if (journeyInstance.id) await refetch(false);
        } finally {
          setIsBusy(false);
        }
      }
    } else if (
      (schedule?.start && scheduleStartUnix.local_unix < currentTimeUnix) ||
      (schedule?.end && scheduleEndUnix.local_unix < currentTimeUnix)
    ) {
      // ToDo (EC-467): wire "Edit Schedule" to the ScheduleSidePanel; preserves v1's no-op for now.
      await showConfirmModal(buildExpiredScheduleDialog(schedule));
    } else if (await showLaunchConfirmModal()) {
      let launchSucceeded = false;
      setIsBusy(true);
      try {
        if (journeyInstance.isApproved) {
          const activationResult = await dispatch(activateJourneyInstance(journeyInstance.id ?? ''));
          launchSucceeded = !!activationResult;
        } else {
          await dispatch(saveJourneyInstance(JOURNEY_STATUSES.ACTIVE));
          // Detect success from post-dispatch store state rather than a stale closure read.
          launchSucceeded = store.getState().te.journey.status === JOURNEY_STATUSES.ACTIVE;
        }
        if (showContextFilesLaunchModal && launchSucceeded) {
          contextDataSlideOut?.open();
        }
      } finally {
        setIsBusy(false);
      }
    }

    await queryClient.invalidateQueries(['active-contents']);
  }, [
    isBusy,
    experienceNameError,
    journeyStatusLabels.launch.isDisabled,
    hasLaunchBlockingError,
    isListBasedTemplate,
    hasPendingFiles,
    isAudienceEmpty,
    journeyInstance,
    showConfirmModal,
    showLaunchConfirmModal,
    showContextFilesLaunchModal,
    contextDataSlideOut,
    dispatch,
    refetch,
    queryClient,
  ]);

  const deactivate = useCallback(async () => {
    const confirmed = await showConfirmModal(
      buildDeactivateDialog(React.createElement(DeactivateIcon, { fill: DEACTIVATE_ICON_FILL })),
    );
    if (!confirmed) return;

    setIsBusy(true);
    try {
      await dispatch(deactivateJourneyInstance(journeyInstance.id ?? ''));
      await queryClient.invalidateQueries(['active-contents']);
    } finally {
      setIsBusy(false);
    }
  }, [showConfirmModal, dispatch, journeyInstance.id, queryClient]);

  // clearJourney helper kept here so the header/popover can reset on exit if needed.
  const _clearJourney = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_JOURNEY });
  }, [dispatch]);
  void _clearJourney;

  return {
    launch,
    deactivate,
    save,
    exit,
    isBusy,
    isJourneyActiveOrScheduled,
    ConfirmModalSlot,
    LaunchConfirmModalSlot,
  };
}
